import { Classes, Dialog, InputGroup } from "@blueprintjs/core"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import React, { useCallback, useState } from "react"
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd"
import { BoardColumnSnapshotType, COLUMN_COLORS, SaveColumn } from "../models/board"
import styles from "./ColumnsDialog.module.scss"

const ARCHIVE_HINT =
  "в такой колонке лежат выполненные задания. Они копятся: доска показывает первые 15, остальные " +
  "добавляются кнопкой «Показать ещё». Истёкший срок в ней не подсвечивается красным"

/** Настройка колонок доски: цвет и порядок у всех, название, признак «Выполненные» и удаление — только у своих */
export const ColumnsDialog: React.FC<ColumnsDialogProps> = ({ columns, customEnabled, onClose, onSubmit }) => {
  const [items, setItems] = useState<SaveColumn[]>(
    columns.map((c) => ({
      id: c.isSystem ? "" : c.id,
      systemKey: c.systemKey,
      name: c.name,
      color: c.color,
      isArchive: !!c.isArchive,
    }))
  )
  const [saving, setSaving] = useState(false)

  const change = useCallback((index: number, patch: Partial<SaveColumn>) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)))
  }, [])

  const remove = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const add = useCallback(() => {
    setItems((prev) => [
      ...prev,
      {
        id: "",
        systemKey: "",
        name: "",
        color: COLUMN_COLORS[prev.length % COLUMN_COLORS.length],
        isArchive: false,
      },
    ])
  }, [])

  const onDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) {
      return
    }

    const from = result.source.index
    const to = result.destination.index

    setItems((prev) => {
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)

      return next
    })
  }, [])

  const submit = useCallback(async () => {
    setSaving(true)
    const success = await onSubmit(items.filter((item) => !!item.systemKey || !!item.name.trim()))
    setSaving(false)

    success && onClose()
  }, [items, onSubmit, onClose])

  return (
    <Dialog
      isCloseButtonShown={true}
      isOpen={true}
      onClose={onClose}
      title="Колонки доски"
      backdropClassName="standard"
      className={`figma-dialog ${styles.dialog}`}
    >
      <div className={styles.body}>
        <div className={styles.hint}>
          Перетащите колонки за левый край, чтобы изменить порядок. Системные колонки нельзя переименовать
          и удалить.
        </div>

        <div className={styles.hint}>
          <b>Выполненные</b> — {ARCHIVE_HINT}
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="columns">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className={styles.list}>
                {items.map((item, index) => (
                  <Draggable key={`${item.systemKey}${item.id}${index}`} draggableId={`c${index}`} index={index}>
                    {(dragProvided, snapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className={`${styles.row} ${snapshot.isDragging ? styles.dragging : ""}`}
                      >
                        <span className={styles.handle} {...dragProvided.dragHandleProps}>
                          <GeneralIcon type="general-dots-more" size={14} />
                        </span>

                        <div className={styles.colors}>
                          {COLUMN_COLORS.map((color) => (
                            <button
                              type="button"
                              key={color}
                              className={`${styles.color} ${item.color === color ? styles.selected : ""}`}
                              style={{ backgroundColor: color }}
                              onClick={() => change(index, { color })}
                            />
                          ))}
                        </div>

                        {item.systemKey ? (
                          <div className={styles.systemName}>
                            {item.name}
                            <span className={styles.badge}>системная</span>
                            {item.isArchive && <span className={styles.badge}>выполненные</span>}
                          </div>
                        ) : (
                          <>
                            <InputGroup
                              className="planr-default-input"
                              value={item.name}
                              placeholder="Название колонки"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                change(index, { name: e.target.value })
                              }
                            />

                            <button
                              type="button"
                              className={`${styles.archive} ${item.isArchive ? styles.archiveOn : ""}`}
                              title={ARCHIVE_HINT}
                              aria-pressed={item.isArchive}
                              onClick={() => change(index, { isArchive: !item.isArchive })}
                            >
                              {/* вид кнопки planr, но не <button>: кнопка внутри кнопки
                                  запрещена HTML, и React пишет об этом ошибку */}
                              <span className={`planr-button neutral small ${styles.archiveIcon}`}>
                                <GeneralIcon type={item.isArchive ? "general-checkbox" : "general-zero-checkbox"} />
                              </span>
                              выполненные
                            </button>
                          </>
                        )}

                        {!item.systemKey && (
                          <PlanrButton
                            type="danger"
                            size="small"
                            icon="general-trash"
                            title="Удалить колонку. Её задания вернутся в «В работе»"
                            onClick={() => remove(index)}
                          />
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {customEnabled ? (
          <PlanrButton type="dashed" size="small" icon="general-plus-small" onClick={add}>
            Добавить колонку
          </PlanrButton>
        ) : (
          <div className={styles.disabled}>
            Добавление своих колонок отключено в настройках приложения
          </div>
        )}
      </div>

      <div className={Classes.DIALOG_FOOTER}>
        <div className={styles.footer}>
          <PlanrButton type="greenish" onClick={submit} disabled={saving}>
            Сохранить
          </PlanrButton>
          <PlanrButton type="graybtn" onClick={onClose}>
            Отмена
          </PlanrButton>
        </div>
      </div>
    </Dialog>
  )
}

interface ColumnsDialogProps {
  columns: BoardColumnSnapshotType[]
  customEnabled: boolean
  onClose: () => void
  onSubmit: (columns: SaveColumn[]) => Promise<boolean>
}
